import { useState, useMemo } from 'react'
import { getApiBase } from '../utils/api'
import { showDemoToast } from '../utils/demo-toast'
import type { ESGMetric } from '@esf/shared'
import Modal from './Modal'

// Simple icon renderer
const renderIcon = (path: string, className = 'w-5 h-5') => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
)

interface ESGPageProps {
  esgMetrics: ESGMetric[]
  fetchEsgMetrics: () => void
  esgSourceFilter: string
  setEsgSourceFilter: (v: string) => void
  esgDateFrom: string
  setEsgDateFrom: (v: string) => void
  esgDateTo: string
  setEsgDateTo: (v: string) => void
  esgSortBy: 'periodStart' | 'carbonSaved' | 'energyGenerated' | 'wasteCollected' | 'recyclingRate'
  setEsgSortBy: (v: 'periodStart' | 'carbonSaved' | 'energyGenerated' | 'wasteCollected' | 'recyclingRate') => void
  esgSortDir: 'asc' | 'desc'
  setEsgSortDir: (v: 'asc' | 'desc') => void
  esgExporting: boolean
  setEsgExporting: (v: boolean) => void
  showSavedReports: boolean
  setShowSavedReports: (v: boolean) => void
  savedReports: Array<{ key: string; size: number; lastModified: string; filename?: string }>
  setSavedReports: (v: Array<{ key: string; size: number; lastModified: string; filename?: string }>) => void
  savedReportsLoading: boolean
  setSavedReportsLoading: (v: boolean) => void
  setAlertModal: (v: { isOpen: boolean; title: string; message: string; type: 'info' | 'success' | 'error' | 'warning' }) => void
}

export default function ESGPage({
  esgMetrics,
  fetchEsgMetrics,
  esgSourceFilter,
  setEsgSourceFilter,
  esgDateFrom,
  setEsgDateFrom,
  esgDateTo,
  setEsgDateTo,
  esgSortBy,
  setEsgSortBy,
  esgSortDir,
  setEsgSortDir,
  esgExporting,
  setEsgExporting,
  showSavedReports,
  setShowSavedReports,
  savedReports,
  setSavedReports,
  savedReportsLoading,
  setSavedReportsLoading,
  setAlertModal,
}: ESGPageProps) {
  const [esgPeriod, setEsgPeriod] = useState<'MONTHLY' | 'QUARTERLY' | 'YEARLY'>('MONTHLY')

  // Filter and sort ESG metrics
  const filteredAndSortedMetrics = useMemo(() => {
    let filtered = esgMetrics.filter(m => {
      // Filter by source
      if (esgSourceFilter && m.source !== esgSourceFilter) return false

      // Filter by date range
      if (esgDateFrom) {
        const fromDate = new Date(esgDateFrom)
        const metricDate = new Date(m.periodStart)
        if (metricDate < fromDate) return false
      }
      if (esgDateTo) {
        const toDate = new Date(esgDateTo)
        const metricDate = new Date(m.periodStart)
        if (metricDate > toDate) return false
      }

      // Filter by period type
      if (m.period !== esgPeriod) return false

      return true
    })

    // Sort
    filtered.sort((a, b) => {
      let aVal: number, bVal: number
      switch (esgSortBy) {
        case 'carbonSaved':
          aVal = a.carbonSaved || 0
          bVal = b.carbonSaved || 0
          break
        case 'energyGenerated':
          aVal = a.energyGenerated || 0
          bVal = b.energyGenerated || 0
          break
        case 'wasteCollected':
          aVal = a.wasteCollected || 0
          bVal = b.wasteCollected || 0
          break
        case 'recyclingRate':
          aVal = a.recyclingRate || 0
          bVal = b.recyclingRate || 0
          break
        case 'periodStart':
        default:
          aVal = new Date(a.periodStart).getTime()
          bVal = new Date(b.periodStart).getTime()
      }
      return esgSortDir === 'asc' ? aVal - bVal : bVal - aVal
    })

    return filtered
  }, [esgMetrics, esgSourceFilter, esgDateFrom, esgDateTo, esgPeriod, esgSortBy, esgSortDir])

  // Calculate aggregate metrics from filtered data
  const totalCarbonSaved = filteredAndSortedMetrics.reduce((sum, m) => sum + (m.carbonSaved || 0), 0)
  const totalEnergyGenerated = filteredAndSortedMetrics.reduce((sum, m) => sum + (m.energyGenerated || 0), 0)
  const totalWasteCollected = filteredAndSortedMetrics.reduce((sum, m) => sum + (m.wasteCollected || 0), 0)
  const avgRecyclingRate = filteredAndSortedMetrics.length > 0
    ? filteredAndSortedMetrics.reduce((sum, m) => sum + (m.recyclingRate || 0), 0) / filteredAndSortedMetrics.length
    : 0

  const hasActiveFilters = esgSourceFilter || esgDateFrom || esgDateTo

  const clearFilters = () => {
    setEsgSourceFilter('')
    setEsgDateFrom('')
    setEsgDateTo('')
  }

  // Sort column click handler
  const handleSort = (column: typeof esgSortBy) => {
    if (esgSortBy === column) {
      setEsgSortDir(esgSortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setEsgSortBy(column)
      setEsgSortDir('desc')
    }
  }

  // Sort indicator
  const renderSortIndicator = (column: typeof esgSortBy) => {
    if (esgSortBy !== column) return <span className="text-gray-300 ml-1">↕</span>
    return <span className="text-primary-600 ml-1">{esgSortDir === 'asc' ? '↑' : '↓'}</span>
  }

  // CSV Export
  const exportToCSV = () => {
    const headers = ['Period', 'Period Start', 'Period End', 'Carbon Saved (kg)', 'Energy Generated (kWh)', 'Waste Collected (kg)', 'Recycling Rate (%)', 'Public Usage', 'Source']
    const rows = filteredAndSortedMetrics.map(m => [
      m.period,
      m.periodStart,
      m.periodEnd,
      m.carbonSaved || '',
      m.energyGenerated || '',
      m.wasteCollected || '',
      m.recyclingRate || '',
      m.publicUsageCount || '',
      m.source
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `esg-report-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  // S3 Export (demo mode)
  const exportToS3 = async () => {
    showDemoToast('In the live platform, this would export the ESG report.')
  }

  // Fetch saved reports (demo mode: return empty)
  const fetchSavedReports = async () => {
    setSavedReportsLoading(true)
    setSavedReports([])
    setSavedReportsLoading(false)
  }

  // Download a saved report (demo mode)
  const downloadReport = (_key: string) => {
    showDemoToast('In the live platform, this would download the saved ESG report.')
  }

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Load saved reports when modal opens
  const handleOpenSavedReports = () => {
    setShowSavedReports(true)
    fetchSavedReports()
  }

  return (
    <div className="container-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">ESG Reports</h1>
        <div className="flex gap-2">
          <button onClick={fetchEsgMetrics} className="btn btn-secondary">
            {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
            Refresh
          </button>
          <button onClick={exportToCSV} className="btn btn-secondary" disabled={filteredAndSortedMetrics.length === 0}>
            {renderIcon('M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z')}
            Export CSV
          </button>
          <button
            onClick={exportToS3}
            className="btn btn-primary"
            disabled={filteredAndSortedMetrics.length === 0 || esgExporting}
          >
            {esgExporting ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Saving...
              </>
            ) : (
              <>
                {renderIcon('M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12')}
                Save to Online Storage
              </>
            )}
          </button>
          <button
            onClick={handleOpenSavedReports}
            className="btn btn-outline"
          >
            {renderIcon('M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4')}
            View Saved Reports
          </button>
        </div>
      </div>

      {/* Filters Row */}
      <div className="card mb-6">
        <div className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
              <select
                className="filter-select"
                value={esgPeriod}
                onChange={e => setEsgPeriod(e.target.value as typeof esgPeriod)}
              >
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly</option>
                <option value="YEARLY">Yearly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <select
                className="filter-select"
                value={esgSourceFilter}
                onChange={e => setEsgSourceFilter(e.target.value)}
              >
                <option value="">All Sources</option>
                <option value="AUTOMATED">Automated</option>
                <option value="MANUAL">Manual</option>
                <option value="IMPORTED">Imported</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                className="filter-select"
                value={esgDateFrom}
                onChange={e => setEsgDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                className="filter-select"
                value={esgDateTo}
                onChange={e => setEsgDateTo(e.target.value)}
              />
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="btn btn-outline text-sm"
              >
                Clear Filters
              </button>
            )}
          </div>
          {hasActiveFilters && (
            <div className="mt-3 text-sm text-gray-500">
              Showing {filteredAndSortedMetrics.length} of {esgMetrics.filter(m => m.period === esgPeriod).length} {esgPeriod.toLowerCase()} records
            </div>
          )}
        </div>
      </div>

      {/* ESG KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="kpi">
          <div className="kpi-icon bg-success-100 text-success-600">
            {renderIcon('M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z')}
          </div>
          <div className="kpi-title">Carbon Saved</div>
          <div className="kpi-value">{(totalCarbonSaved / 1000).toFixed(1)}t</div>
          <div className="kpi-change positive">CO2e avoided</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon bg-warning-100 text-warning-600">
            {renderIcon('M13 10V3L4 14h7v7l9-11h-7z')}
          </div>
          <div className="kpi-title">Energy Generated</div>
          <div className="kpi-value">{totalEnergyGenerated.toLocaleString()}</div>
          <div className="kpi-change positive">kWh solar</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon bg-info-100 text-info-600">
            {renderIcon('M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16')}
          </div>
          <div className="kpi-title">Waste Collected</div>
          <div className="kpi-value">{(totalWasteCollected / 1000).toFixed(1)}t</div>
          <div className="kpi-change positive">Total waste</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon bg-purple-100 text-purple-600">
            {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
          </div>
          <div className="kpi-title">Recycling Rate</div>
          <div className="kpi-value">{avgRecyclingRate.toFixed(0)}%</div>
          <div className="kpi-change positive">Average</div>
        </div>
      </div>

      {/* ESG Metrics Table */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="card-title">ESG Metrics History</h2>
          <span className="text-sm text-gray-500">
            Click column headers to sort
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('periodStart')}
                >
                  Period {renderSortIndicator('periodStart')}
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('carbonSaved')}
                >
                  Carbon Saved {renderSortIndicator('carbonSaved')}
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('energyGenerated')}
                >
                  Energy Generated {renderSortIndicator('energyGenerated')}
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('wasteCollected')}
                >
                  Waste Collected {renderSortIndicator('wasteCollected')}
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('recyclingRate')}
                >
                  Recycling Rate {renderSortIndicator('recyclingRate')}
                </th>
                <th>Public Usage</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedMetrics.map(metric => (
                <tr key={metric.id}>
                  <td className="font-medium">
                    {new Date(metric.periodStart).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                  </td>
                  <td>{metric.carbonSaved ? `${metric.carbonSaved.toLocaleString()} kg` : '-'}</td>
                  <td>{metric.energyGenerated ? `${metric.energyGenerated.toLocaleString()} kWh` : '-'}</td>
                  <td>{metric.wasteCollected ? `${metric.wasteCollected.toLocaleString()} kg` : '-'}</td>
                  <td>{metric.recyclingRate ? `${metric.recyclingRate}%` : '-'}</td>
                  <td>{metric.publicUsageCount?.toLocaleString() || '-'}</td>
                  <td>
                    <span className={`badge ${
                      metric.source === 'AUTOMATED' ? 'bg-success-100 text-success-700' :
                      metric.source === 'MANUAL' ? 'bg-info-100 text-info-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {metric.source}
                    </span>
                  </td>
                </tr>
              ))}
              {filteredAndSortedMetrics.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-state-icon bg-success-100 text-success-600">
                        {renderIcon('M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z')}
                      </div>
                      <div className="empty-state-title">No ESG Data</div>
                      <div className="empty-state-text">
                        {hasActiveFilters
                          ? 'No records match your filter criteria. Try adjusting the filters.'
                          : 'ESG metrics will be collected automatically from your IoT devices and asset data.'}
                      </div>
                      {hasActiveFilters && (
                        <button onClick={clearFilters} className="btn btn-secondary mt-4">
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Saved Reports Modal */}
      <Modal
        isOpen={showSavedReports}
        onClose={() => setShowSavedReports(false)}
        title="Saved Reports"
        size="lg"
      >
        <div className="space-y-4">
          {savedReportsLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="animate-spin inline-block w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full mr-3" />
              Loading saved reports...
            </div>
          ) : savedReports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="mb-2">
                {renderIcon('M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4')}
              </div>
              <p>No saved reports yet.</p>
              <p className="text-sm mt-1">Use "Save to Online Storage" to save your first report.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {savedReports.map((report, index) => (
                <div key={report.key || index} className="py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {report.filename || report.key?.split('/').pop()}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(report.lastModified).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {report.size && ` • ${formatFileSize(report.size)}`}
                    </p>
                  </div>
                  <button
                    onClick={() => downloadReport(report.key)}
                    className="btn btn-sm btn-secondary ml-4"
                  >
                    {renderIcon('M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z')}
                    Download
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end pt-4 border-t">
            <button
              onClick={() => setShowSavedReports(false)}
              className="btn btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
